/**
 * RouteOverview Page
 * 
 * Main page showing operator's route with all stores to visit
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useParams } from 'wouter';
import { useAuthUser } from '@/hooks/useAuthUser';
import { apiFetch } from '@/lib/api';
import { MobileScaffold } from '@/components/mobile/MobileScaffold';
import { RouteStats } from '@/components/mobile/RouteStats';
import { StoreCard } from '@/components/mobile/StoreCard';
import { StoreCardSkeleton, RouteStatsSkeleton } from '@/components/mobile/StoreFirstSkeletons';
import { Input } from '@/components/ui/input';
import { Loader2, Search, Store } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useStoreFirstError } from '@/hooks/useStoreFirstError';
import { StoreFirstErrorBoundary } from '@/components/mobile/StoreFirstErrorBoundary';
import type { StoreVisit, RouteStats as RouteStatsType } from '@/types/store-first';
import { transformMobileTasksToStoreVisits } from '@/utils/transformMobileTasks';
import { useCompletion } from '@/hooks/useCompletion';
import { ManagerSignatureModal } from '@/components/ManagerSignatureModal';
// import { PendingSignoffModal } from '@/components/mobile/PendingSignoffModal'; // ✅ DISABLED: Enforcing strict signature requirement
import { useStoreSignoffStatus } from '@/hooks/useStoreSignoffStatus';
import { useCycleSummary } from '@/hooks/useCycleSummary';
import { useStoreActions } from '@/hooks/useStoreActions';

function RouteOverviewContent() {
  const [location, setLocation] = useLocation();
  const params = useParams<{ cycleId?: string; operatorId?: string }>();
  const cycleId = params?.cycleId ? parseInt(params.cycleId) : undefined;
  const operatorIdFromUrl = params?.operatorId ? parseInt(params.operatorId) : undefined;
  const { toast } = useToast();
  const { handleError } = useStoreFirstError();
  const { user } = useAuthUser();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');

  const operatorId = user?.id ?? (user as any)?.userId ?? (user as any)?.localId;
  const isMobileTestMode = Boolean((user as any)?.mobileTestModeEnabled);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureStoreId, setSignatureStoreId] = useState<number | null>(null);
  const [signatureStoreName, setSignatureStoreName] = useState<string | null>(null);
  
  // Proscriptive Workflow: Store actions
  const { startVisit, completeStore } = useStoreActions();
  // ✅ DISABLED: const [pendingSignoffModalOpen, setPendingSignoffModalOpen] = useState(false);
  const [completedStoreIds, setCompletedStoreIds] = useState<Set<number>>(new Set());
  // Use ref to track stores we've already triggered modals for (avoids dependency issues)
  const triggeredModalForStores = useRef<Set<number>>(new Set());
  const hasNavigatedToLanding = useRef(false);
  const hasNavigatedToCompletion = useRef(false);

  // Track which stores have already shown toasts to prevent duplicates
  const shownToastsForStores = useRef<Set<number>>(new Set());
  const previousCompleteIds = useRef<Set<number>>(new Set());
  const isInitialCompletionSync = useRef(true);
  const hasTriggeredPendingSignoff = useRef(false);

  // Fetch completion data using new completion endpoint
  const {
    data: completionData = [],
    isLoading: completionLoading,
    error: completionError
  } = useCompletion(cycleId);

  // Fetch store visits using existing mobile tasks endpoint
  const {
    data: storeVisits = [],
    isLoading,
    error,
    refetch
  } = useQuery<StoreVisit[]>({
    queryKey: ['storeVisits', operatorId, cycleId],
    queryFn: async () => {
      if (!operatorId || !cycleId) {
        throw new Error('Operator ID or Cycle ID not found');
      }

      const response = await apiFetch(`/api/mobile/tasks/${cycleId}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch tasks: ${response.status}`);
      }

      const stores = await response.json();
      console.log('[ROUTE_OVERVIEW] API response:', stores?.length || 0, 'store entries');
      
      // Ensure stores is an array
      if (!Array.isArray(stores)) {
        console.error('[ROUTE_OVERVIEW] API did not return an array:', stores);
        return [];
      }
      
      // Transform existing API response to store-first structure
      const transformed = transformMobileTasksToStoreVisits(stores);
      console.log('[ROUTE_OVERVIEW] Transformed to:', transformed.length, 'stores');
      
      return transformed;
    },
    enabled: !!operatorId && !!cycleId,
    staleTime: 30_000, // 30 seconds
    retry: 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  // Extract cycleId from store visits if not in URL params
  // If all stores belong to the same cycle, use that cycleId
  const effectiveCycleId = useMemo(() => {
    if (cycleId) return cycleId;
    if (storeVisits.length > 0) {
      // Get unique cycle IDs from store visits
      const cycleIds = new Set<number>(
        storeVisits
          .map(v => v.cycleId)
          .filter((id): id is number => typeof id === 'number')
      );
      if (cycleIds.size === 1) {
        return Array.from(cycleIds)[0];
      }
    }
    return undefined;
  }, [cycleId, storeVisits]);

  // Fetch cycle summary to detect pending_signoff status
  const { data: cycleSummary } = useCycleSummary(operatorId, effectiveCycleId);

  // Reset completion flag when cycle returns to active/pending states
  useEffect(() => {
    if (!cycleSummary || !effectiveCycleId) return;
    if (cycleSummary.status !== 'signed_off') {
      localStorage.removeItem(`cycle_${effectiveCycleId}_complete_shown`);
    }
  }, [cycleSummary?.status, effectiveCycleId]);

  // Auto-navigate to cycle landing if new cycle assigned and not started yet
  useEffect(() => {
    if (!cycleSummary || !effectiveCycleId || !operatorId) return;

    const startedKey = `cycle_${effectiveCycleId}_started`;
    const hasStarted = localStorage.getItem(startedKey) === 'true';

    if (!hasStarted && !hasNavigatedToLanding.current && cycleSummary.totalStores > 0) {
      hasNavigatedToLanding.current = true;
      setLocation(`/cycle/${effectiveCycleId}/landing`);
    }
  }, [cycleSummary, effectiveCycleId, operatorId, setLocation]);

  // Auto-navigate to cycle complete screen when cycle signed off (once)
  useEffect(() => {
    if (!cycleSummary || !effectiveCycleId || !operatorId) return;

    const completeKey = `cycle_${effectiveCycleId}_complete_shown`;
    const hasShown = localStorage.getItem(completeKey) === 'true';

    if (
      cycleSummary.status === 'signed_off' &&
      !hasNavigatedToCompletion.current &&
      !hasShown
    ) {
      hasNavigatedToCompletion.current = true;
      localStorage.setItem(completeKey, 'true');
      setLocation(`/cycle/${effectiveCycleId}/complete`);
    }
  }, [cycleSummary, effectiveCycleId, operatorId, setLocation]);

  // DEBUG: Log cycle summary status
  console.log('[DEBUG] RouteOverview:', {
    cycleIdFromUrl: cycleId,
    effectiveCycleId,
    operatorId,
    storeVisitsCount: storeVisits.length,
    storeVisitsCycleIds: storeVisits.map(v => v.cycleId).filter(Boolean),
    cycleSummary,
    cycleSummaryStatus: cycleSummary?.status,
    cycleSummaryError: cycleSummary ? null : 'No cycle summary data',
    // ✅ REMOVED: pendingSignoffModalOpen (no longer exists),
    signatureModalOpen,
    hasTriggeredPendingSignoff: hasTriggeredPendingSignoff.current,
    completionDataCount: completionData.length,
    completedStoresCount: completionData.filter(c => c.isComplete).length
  });

  // Prefetch individual store visit queries for offline caching
  useEffect(() => {
    if (!isLoading && !error && storeVisits.length > 0 && operatorId) {
      console.log('[OFFLINE_CACHE] Prefetching', storeVisits.length, 'store visits for offline access');
      
      // Prefetch each store visit query (used by store detail page)
      storeVisits.forEach(async (visit) => {
        try {
          await queryClient.prefetchQuery({
            queryKey: ['storeVisit', visit.storeId, operatorId],
            queryFn: async () => {
              console.log('[OFFLINE_CACHE] Prefetched store visit for store', visit.storeId);
              return { visit };
            },
            staleTime: 5 * 60 * 1000, // 5 minutes
          });
          
          // Verify it was cached
          const cached = queryClient.getQueryData(['storeVisit', visit.storeId, operatorId]);
          if (cached) {
            console.log('[OFFLINE_CACHE] Verified cache for store', visit.storeId);
          } else {
            console.warn('[OFFLINE_CACHE] Failed to cache store', visit.storeId);
          }
        } catch (err) {
          console.error('[OFFLINE_CACHE] Error prefetching store', visit.storeId, err);
        }
      });
      
      console.log('[OFFLINE_CACHE] Prefetching initiated for', storeVisits.length, 'stores');
    }
  }, [storeVisits, isLoading, error, operatorId, queryClient]);

  // Calculate route statistics using completion data
  const routeStats: RouteStatsType = useMemo(() => {
    const totalStores = storeVisits.length;
    const completedStores = completionData.filter(c => c.isComplete).length;
    const pendingStores = storeVisits.filter(v => v.visitStatus === 'pending').length;
    const inProgressStores = storeVisits.filter(v => v.visitStatus === 'in_progress').length;
    const pausedStores = storeVisits.filter(v => v.visitStatus === 'paused').length;
    
    return {
      totalStores,
      completedStores,
      pendingStores,
      inProgressStores,
      pausedStores,
      estimatedTimeRemaining: 0,
    };
  }, [storeVisits, completionData]);

  // ✅ DISABLED: Auto-trigger signature modal on route-overview
  // Now that we have instant signature trigger from task submission,
  // we don't need the route-overview to auto-trigger modals.
  // This prevents conflicts where the modal opens for the wrong store.
  useEffect(() => {
    if (completionData && completionData.length > 0 && !isLoading && !completionLoading) {
      // Just update completion tracking for display purposes
      const fullyCompleteIds = new Set(
        completionData
          .filter(s => s.isComplete)
          .map(s => s.storeId)
      );
      
      setCompletedStoreIds(fullyCompleteIds);
      
      // On initial sync, mark all awaiting signature stores as "already shown"
      // to prevent old modal triggers
      if (isInitialCompletionSync.current) {
        completionData
          .filter(s => s.postingComplete && !s.isSigned)
          .forEach((s) => {
            triggeredModalForStores.current.add(s.storeId);
            shownToastsForStores.current.add(s.storeId);
          });
        isInitialCompletionSync.current = false;
      }
    }
  }, [completionData, isLoading, completionLoading]);

  // Check sign-off status for the store that's about to trigger modal
  const { data: signoffStatus } = useStoreSignoffStatus(signatureStoreId, operatorId ? Number(operatorId) : undefined);
  
  // Prevent modal from opening if store is already signed off
  useEffect(() => {
    if (signatureModalOpen && signoffStatus?.isSignedOff && signatureStoreId) {
      // Store already signed off - close modal and show message (only once)
      if (!shownToastsForStores.current.has(signatureStoreId)) {
        shownToastsForStores.current.add(signatureStoreId);
        toast({
          title: 'Already Signed Off',
          description: `${signatureStoreName || 'Store'} has already been signed off.`,
        });
      }
      setSignatureModalOpen(false);
      setSignatureStoreId(null);
      setSignatureStoreName(null);
    }
  }, [signatureModalOpen, signoffStatus, signatureStoreName, signatureStoreId, toast]);

  // ✅ DISABLED: All automatic signature modal triggers removed from route-overview
  // Only the instant trigger from store-detail (after completing the actual last task) should open the modal
  // The fallback triggers were interfering and opening modals with wrong assignment IDs

  // Proscriptive Workflow: Find active store (one-active-store rule)
  const activeStore = useMemo(() => {
    return storeVisits.find(v => v.visitStatus === 'in_progress');
  }, [storeVisits]);

  // Filter stores by search term
  const filteredVisits = useMemo(() => {
    if (!searchTerm.trim()) {
      return storeVisits;
    }

    const term = searchTerm.toLowerCase();
    return storeVisits.filter(visit =>
      visit.storeName.toLowerCase().includes(term) ||
      visit.storeAddress.toLowerCase().includes(term) ||
      visit.retailerName?.toLowerCase().includes(term)
    );
  }, [storeVisits, searchTerm]);

  // Handle errors
  if (error) {
    handleError(error, 'Loading store visits');
  }

  const handleStoreNavigate = (storeId: number) => {
    // ✅ CRITICAL: Pass cycleId in URL so store-detail knows current cycle
    const url = effectiveCycleId 
      ? `/store/${storeId}?cycleId=${effectiveCycleId}`
      : `/store/${storeId}`;
    setLocation(url);
  };

  return (
    <MobileScaffold title="My Route">
      <div className="p-4 space-y-4 bg-white min-h-screen">
        {/* Debug info - always visible for troubleshooting */}
        <div className="bg-yellow-100 p-2 text-xs mb-2 border border-yellow-300">
          <strong>Debug Info:</strong>
          <br />
          isLoading={String(isLoading)}, storeVisits={storeVisits.length}, operatorId={String(operatorId)}, cycleId={cycleId ? String(cycleId) : 'none'}
          <br />
          effectiveCycleId={effectiveCycleId ? String(effectiveCycleId) : 'none'}
          <br />
          cycleSummaryStatus={cycleSummary?.status || 'loading...'}
          <br />
          cycleSummary: {cycleSummary ? JSON.stringify({ status: cycleSummary.status, totalStores: cycleSummary.totalStores, signedOffBy: cycleSummary.signedOffBy, completedAt: cycleSummary.completedAt }) : 'null'}
          <br />
          signatureModalOpen={String(signatureModalOpen)}
          <br />
          completedStores={completionData.filter(c => c.isComplete).length} / {completionData.length}
          <br />
          hasTriggeredPendingSignoff={String(hasTriggeredPendingSignoff.current)}
        </div>
        {/* Route Statistics */}
        {isLoading ? (
          <RouteStatsSkeleton />
        ) : storeVisits.length > 0 && (
          <RouteStats 
            stats={routeStats}
          />
        )}

        {/* Search Bar */}
        {!isLoading && storeVisits.length > 0 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search stores..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <StoreCardSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && storeVisits.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <Store className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No stores assigned</h3>
            <p className="text-gray-500 text-center">
              You don't have any stores assigned yet. Check back later or contact your administrator.
            </p>
          </div>
        )}

        {/* No Search Results */}
        {!isLoading && storeVisits.length > 0 && filteredVisits.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <Search className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No stores found</h3>
            <p className="text-gray-500 text-center">
              No stores match "{searchTerm}". Try a different search term.
            </p>
          </div>
        )}

        {/* Store List */}
        {!isLoading && filteredVisits.length > 0 && (
          <div className="space-y-3">
            {isMobileTestMode && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                Test mode is active. This account can inspect copied mobile work on device, but all mobile actions are blocked.
              </div>
            )}
            {filteredVisits.map((visit) => {
              // Find matching completion data for this store
              const storeCompletion = completionData.find(c => c.storeId === visit.storeId);
              
              // DEBUG: Log completion data to troubleshoot signature button
              console.log('[STORE_CARD_DEBUG]', {
                storeId: visit.storeId,
                storeName: visit.storeName,
                completionData: storeCompletion,
                needsSignature: storeCompletion?.postingComplete && !storeCompletion?.isSigned
              });
              
              // Proscriptive Workflow: Lock stores when another is active
              const isActive = visit.visitStatus === 'in_progress';
              const isLocked = !isActive && activeStore !== undefined;
              const disabledReason = isLocked 
                ? `Complete ${activeStore.storeName} first` 
                : undefined;

              return (
                <StoreCard
                  key={visit.storeId}
                  visit={visit}
                  onNavigate={() => handleStoreNavigate(visit.storeId)}
                  onSignOff={isMobileTestMode ? undefined : () => {
                    console.log('[MANUAL_SIGNOFF] User clicked Sign Off button for store:', visit.storeId);
                    setSignatureStoreId(visit.storeId);
                    setSignatureStoreName(visit.storeName);
                    setSignatureModalOpen(true);
                  }}
                  onStartVisit={isMobileTestMode ? undefined : () => {
                    if (!operatorId || !effectiveCycleId) {
                      toast({
                        title: 'Error',
                        description: 'Missing operator ID or cycle ID',
                        variant: 'destructive',
                      });
                      return;
                    }
                    startVisit.mutate({
                      storeId: visit.storeId,
                      operatorId: Number(operatorId),
                      cycleId: effectiveCycleId,
                    });
                  }}
                  onMarkComplete={isMobileTestMode ? undefined : () => {
                    if (!operatorId || !effectiveCycleId) {
                      toast({
                        title: 'Error',
                        description: 'Missing operator ID or cycle ID',
                        variant: 'destructive',
                      });
                      return;
                    }
                    completeStore.mutate({
                      storeId: visit.storeId,
                      operatorId: Number(operatorId),
                      cycleId: effectiveCycleId,
                    });
                  }}
                  completionData={storeCompletion}
                  disabled={isLocked}
                  disabledReason={disabledReason}
                  isStartingVisit={startVisit.isPending}
                  isMarkingComplete={completeStore.isPending}
                />
              );
            })}
          </div>
        )}

        {/* Refresh button (optional - can be removed if not needed) */}
        {!isLoading && storeVisits.length > 0 && (
          <div className="text-center pt-4">
            <button
              onClick={() => refetch()}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Refresh
            </button>
          </div>
        )}
      </div>

      {/* Manager Signature Modal - triggers per store when store reaches 100% */}
      <ManagerSignatureModal
        open={signatureModalOpen}
        onClose={() => {
          setSignatureModalOpen(false);
          setSignatureStoreId(null);
          setSignatureStoreName(null);
        }}
        storeId={signatureStoreId}
        storeName={signatureStoreName}
        operatorId={operatorId ? Number(operatorId) : undefined}
        cycleId={effectiveCycleId ?? undefined}
      />

      {/* 
        ✅ DISABLED: Pending Sign-Off Recovery Modal
        Removed to enforce strict signature requirement.
        Stores must have manager signatures to count as "complete".
        No bypass or auto-sign-off allowed.
      */}
    </MobileScaffold>
  );
}

export default function RouteOverview() {
  return (
    <StoreFirstErrorBoundary>
      <RouteOverviewContent />
    </StoreFirstErrorBoundary>
  );
}
